/**
 * What a record-backed board's columns are: the fields its row source exposes,
 * named by the ids the rows endpoint reads them back under. A record board's
 * `columnOrder` holds these ids and its `customColumns` is empty, so this table
 * is what turns a saved layout into a header, a cell renderer, and a query.
 *
 * Every capability here mirrors one the server actually has. A field is only
 * `sortable` if the source declares a sort expression for it, only `filter: 'in'`
 * if the server can match values on it, and only `countable` if it can return a
 * whole-set count grouped by it. Claiming a capability the API lacks would
 * surface as a 400 on the user's first click, so this table is the contract and
 * is checked against the source definitions by test.
 */
import type { ParseKeys } from 'i18next';

import type { CustomColumnType } from './board-columns';
import { isSortableColumnType } from './sorting';

/**
 * How a selection on a field becomes query parameters.
 *
 * `in` hands values straight back to the server. `date` has no enumerable
 * values, so it narrows by the same overdue / this month / later buckets the
 * client boards use, compiled to `gte` / `lte` boundaries. A field with neither
 * is display-only: it can still be shown and possibly sorted, just not filtered.
 */
export type RecordFilterKind = 'in' | 'date';

export interface RecordField {
  /** The name the rows endpoint reads and writes this field under. */
  id: string;
  labelKey: ParseKeys<'trackers'>;
  /** Which cell renderer the board uses. */
  type: CustomColumnType;
  sortable?: boolean;
  filter?: RecordFilterKind;
  /**
   * The server can return whole-set value counts grouped by this field. False
   * for multi-valued fields, which the source exposes as an existence test with
   * no single value to group on.
   */
  countable?: boolean;
  /** The source accepts this field in a row update, so the cell can be edited. */
  writable?: boolean;
  /**
   * The record can hold this field empty. Only read for a writable field, where
   * it decides whether the cell offers Clear: a column the record requires
   * would otherwise show an action whose write comes back a server error.
   */
  nullable?: boolean;
  /**
   * The platform's writes to this field are reported to the board's
   * automations, so a rule can watch it. A date field here is one the daily
   * scan reads for a date rule.
   */
  trigger?: boolean;
}

const PROJECT_FIELDS: readonly RecordField[] = [
  {
    id: 'title',
    labelKey: 'trackersBoard.recordFields.title',
    type: 'text',
    sortable: true,
    trigger: true,
  },
  {
    id: 'status',
    labelKey: 'trackersBoard.recordFields.status',
    type: 'status',
    sortable: true,
    filter: 'in',
    countable: true,
    writable: true,
    trigger: true,
  },
  {
    id: 'type',
    labelKey: 'trackersBoard.recordFields.type',
    type: 'text',
    sortable: true,
    filter: 'in',
    countable: true,
    trigger: true,
  },
  {
    id: 'ownerId',
    labelKey: 'trackersBoard.recordFields.owner',
    type: 'people',
    sortable: true,
    filter: 'in',
    countable: true,
  },
  {
    id: 'market',
    labelKey: 'trackersBoard.recordFields.market',
    type: 'text',
    sortable: true,
    filter: 'in',
    countable: true,
    trigger: true,
  },
  {
    id: 'startDate',
    labelKey: 'trackersBoard.recordFields.startDate',
    type: 'date',
    sortable: true,
    filter: 'date',
    writable: true,
    nullable: true,
    trigger: true,
  },
  {
    id: 'deadline',
    labelKey: 'trackersBoard.recordFields.deadline',
    type: 'date',
    sortable: true,
    filter: 'date',
    writable: true,
    nullable: true,
    trigger: true,
  },
  {
    id: 'lastActivityAt',
    labelKey: 'trackersBoard.recordFields.lastActivity',
    type: 'date',
    sortable: true,
    filter: 'date',
  },
  {
    id: 'createdAt',
    labelKey: 'trackersBoard.recordFields.created',
    type: 'date',
    sortable: true,
    filter: 'date',
  },
  {
    id: 'updatedAt',
    labelKey: 'trackersBoard.recordFields.updated',
    type: 'date',
    sortable: true,
    filter: 'date',
  },
  {
    id: 'assetCount',
    labelKey: 'trackersBoard.recordFields.assets',
    type: 'number',
    sortable: true,
  },
  {
    id: 'pendingAssetCount',
    labelKey: 'trackersBoard.recordFields.pendingAssets',
    type: 'number',
    sortable: true,
  },
  // Multi-valued: the source exposes it as an existence test, which the server
  // can match values on but cannot group a count by.
  {
    id: 'tags',
    labelKey: 'trackersBoard.recordFields.tags',
    type: 'tags',
    filter: 'in',
    writable: true,
  },
];

const ASSET_FIELDS: readonly RecordField[] = [
  // The signed URL the source hydrates, not the `thumbnailAssetId` it was
  // signed from: the id is on the row so a client holding a stale page can tell
  // the URL has expired, and is a uuid rather than anything to show a person.
  {
    id: 'thumbnailUrl',
    labelKey: 'trackersBoard.recordFields.preview',
    type: 'files',
  },
  {
    id: 'fileName',
    labelKey: 'trackersBoard.recordFields.name',
    type: 'text',
    sortable: true,
    writable: true,
    trigger: true,
  },
  {
    id: 'fileType',
    labelKey: 'trackersBoard.recordFields.fileType',
    type: 'text',
    sortable: true,
    filter: 'in',
    countable: true,
  },
  {
    id: 'fileSize',
    labelKey: 'trackersBoard.recordFields.fileSize',
    type: 'number',
    sortable: true,
  },
  {
    id: 'purpose',
    labelKey: 'trackersBoard.recordFields.purpose',
    type: 'text',
    sortable: true,
    filter: 'in',
    countable: true,
  },
  {
    id: 'reviewStatus',
    labelKey: 'trackersBoard.recordFields.reviewStatus',
    type: 'status',
    sortable: true,
    filter: 'in',
    countable: true,
    trigger: true,
  },
  {
    id: 'projectTitle',
    labelKey: 'trackersBoard.recordFields.project',
    type: 'text',
    sortable: true,
    filter: 'in',
    countable: true,
  },
  {
    id: 'uploadedBy',
    labelKey: 'trackersBoard.recordFields.uploadedBy',
    type: 'people',
    sortable: true,
    filter: 'in',
    countable: true,
  },
  {
    id: 'uploadedAt',
    labelKey: 'trackersBoard.recordFields.uploaded',
    type: 'date',
    sortable: true,
    filter: 'date',
  },
  {
    id: 'latestVersionNumber',
    labelKey: 'trackersBoard.recordFields.version',
    type: 'number',
    sortable: true,
  },
  {
    id: 'approvalStatus',
    labelKey: 'trackersBoard.recordFields.approval',
    type: 'status',
    sortable: true,
    filter: 'in',
    countable: true,
    trigger: true,
  },
  {
    id: 'approvalStepPosition',
    labelKey: 'trackersBoard.recordFields.approvalStep',
    type: 'number',
    sortable: true,
  },
  {
    id: 'unresolvedCommentCount',
    labelKey: 'trackersBoard.recordFields.openComments',
    type: 'number',
    sortable: true,
  },
  {
    id: 'tags',
    labelKey: 'trackersBoard.recordFields.tags',
    type: 'tags',
    filter: 'in',
    writable: true,
  },
];

/**
 * Which workspace a record lives in, on a board reading several. Every source
 * has it, so it is not in any source's table: the rows endpoint adds it beside
 * the source's own fields, and the board shows it only while the response
 * names more than one workspace. It is never written into a layout, since the
 * scope that makes it appear is each person's own while the layout is shared.
 * Sorting asks the server, which orders by workspace name.
 */
export const WORKSPACE_FIELD: RecordField = {
  id: 'workspace',
  labelKey: 'trackersBoard.recordFields.workspace',
  type: 'text',
  sortable: true,
  filter: 'in',
  countable: true,
};

const FIELDS_BY_SOURCE: Readonly<Record<string, readonly RecordField[]>> = {
  projects: PROJECT_FIELDS,
  assets: ASSET_FIELDS,
};

/**
 * The field whose value names the row in the sticky Item column. It is never
 * one of the board's own columns, so it is listed here rather than inferred
 * from `columnOrder`.
 */
const TITLE_FIELD_BY_SOURCE: Readonly<Record<string, string>> = {
  // Wire field ids, not copy.
  // eslint-disable-next-line i18next/no-literal-string
  projects: 'title',
  assets: 'fileName',
};

export function titleFieldFor(itemSource: string | null | undefined): string | null {
  return TITLE_FIELD_BY_SOURCE[itemSource ?? ''] ?? null;
}

/** Every field a source offers, in the order a board with no layout shows them. */
export function recordFieldsFor(itemSource: string | null | undefined): readonly RecordField[] {
  return FIELDS_BY_SOURCE[itemSource ?? ''] ?? [];
}

/**
 * A field the board will both offer to sort by and actually sort by.
 *
 * Two conditions, because either alone lets a dead row into the list. The
 * server has to declare a sort expression for the field, or the request comes
 * back a 400; and the type has to be one a person can read an order in. A
 * people field satisfies the first and fails the second: its column holds user
 * ids, so the server would faithfully order the board by uuid.
 */
export function isRecordSortable(field: RecordField): boolean {
  return field.sortable === true && isSortableColumnType(field.type);
}

/** Whether this tracker's rows come from the rows endpoint at all. */
export function isRecordSource(itemSource: string | null | undefined): boolean {
  return recordFieldsFor(itemSource).length > 0;
}

/**
 * Fields a board can group by: the server has to be able to both narrow on a
 * value and return a count for it, or the group headers would have to count the
 * loaded page.
 */
export function groupableFields(fields: readonly RecordField[]): readonly RecordField[] {
  return fields.filter((field) => field.countable === true && field.filter === 'in');
}
